import { AtributeElement, FacingGroup, Nesting, Block } from './job.types';
import { STEP_AISLE_HEIGHT } from '../../utils/constants';
import { SEGMENT_WIDTH } from '../../components/ViewAndEdit/editorConstants';

export function revertOrientationFacing(
  orientation: number,
  width: number,
  height: number,
  depth: number
) {
  switch (orientation) {
    case 1:
    case 7:
    case 13:
    case 19:
      return {
        width: height,
        height: width,
        depth
      };
    case 2:
    case 8:
    case 14:
    case 20:
      return {
        width: depth,
        height,
        depth: width
      };
    case 3:
    case 9:
    case 15:
    case 21:
      return {
        width: depth,
        height: width,
        depth: height
      };
    case 4:
    case 10:
    case 16:
    case 22:
      return {
        width,
        height: depth,
        depth: height
      };
    case 5:
    case 11:
    case 17:
    case 23:
      return {
        width: height,
        height: depth,
        depth: width
      };
    default:
      return {
        width,
        height,
        depth
      };
  }
}

export function rotateHorizontallyOrientationFacing(
  orientation: number,
) {
  switch (orientation) {
    case 0:
    case 6:
    case 12:
    case 18:
    case 24:
      return 2;
    case 1:
    case 7:
    case 13:
    case 19:
      return 5;
    case 3:
    case 9:
    case 15:
    case 21:
      return 4;
    case 4:
    case 10:
    case 16:
    case 22:
      return 3;
    case 5:
    case 11:
    case 17:
    case 23:
      return 1;
    default:
      return 0;
  }
}

export function rotateVerticallyOrientationFacing(
  orientation: number,
) {
  switch (orientation) {
    case 0:
    case 6:
    case 12:
    case 18:
    case 24:
      return 4;
    case 1:
    case 7:
    case 13:
    case 19:
      return 3;
    case 2:
    case 8:
    case 14:
    case 20:
      return 5;
    case 3:
    case 9:
    case 15:
    case 21:
      return 1;
    case 5:
    case 11:
    case 17:
    case 23:
      return 2;
    default:
      return 0;
  }
}

export function rotateClockwiseOrientationFacing(
  orientation: number,
) {
  switch (orientation) {
    case 0:
    case 6:
    case 12:
    case 18:
    case 24:
      return 1;
    case 2:
    case 8:
    case 14:
    case 20:
      return 3;
    case 3:
    case 9:
    case 15:
    case 21:
      return 2;
    case 4:
    case 10:
    case 16:
    case 22:
      return 5;
    case 5:
    case 11:
    case 17:
    case 23:
      return 4;
    default:
      return 0;
  }
}

export function rotateCappedOrientationFacing(
  orientation: number,
) {
  switch (orientation) {
    case 1:
    case 7:
    case 13:
    case 19:
      return 3;
    case 2:
    case 8:
    case 14:
    case 20:
      return 5;
    case 3:
    case 9:
    case 15:
    case 21:
      return 1;
    case 4:
    case 10:
    case 16:
    case 22:
      return 0;
    case 5:
    case 11:
    case 17:
    case 23:
      return 2;
    default:
      return 4;
  }
}

export function generateRange(
  wos: Record<string, number>,
  productsList: Array<string>
): Record<string, AtributeElement> {
  const rangeWOS = productsList.reduce<Record<number, AtributeElement>>(
    (acc, productId, index) => {
      const productWOS = wos[productId] || 0;
      let range;

      switch (true) {
        case productWOS < 1:
          range = {
            unique: productWOS.toFixed(2),
            name: 'WOS < 1'
          };
          break;
        case productWOS >= 1 && productWOS < 2:
          range = {
            unique: productWOS.toFixed(2),
            name: '1 <= WOS < 2'
          };
          break;
        case productWOS >= 2 && productWOS < 5:
          range = {
            unique: productWOS.toFixed(2),
            name: '2 <= WOS < 5'
          };
          break;
        case productWOS >= 5 && productWOS < 26:
          range = {
            unique: productWOS.toFixed(2),
            name: '5 <= WOS < 26'
          };
          break;
        case productWOS >= 26 && productWOS < 52:
          range = {
            unique: productWOS.toFixed(2),
            name: '26 <= WOS < 52'
          };
          break;
        case productWOS >= 52:
          range = {
            unique: productWOS.toFixed(2),
            name: 'WOS >= 52'
          };
          break;
      }

      if (range) {
        return {
          ...acc,
          [productId.toString()]: range
        };
      }

      return acc;
    },
        {} as Record<string, AtributeElement>
  );

  return rangeWOS;
}

export function generateRangeDGM(
  dgm: Record<string, number> | undefined,
  productsList: Array<string>,
): Record<string, AtributeElement> {
  const rangeDGM = productsList.reduce<Record<number, AtributeElement>>(
    (acc, productId) => {
      const productDGM = dgm?.[productId] || 0;
      let range;

      switch (true) {
        case productDGM < 0:
          range = {
            unique: productDGM.toFixed(2),
            name: 'DGM < 0'
          };
          break;
        case productDGM >= 0 && productDGM < 0.05:
          range = {
            unique: productDGM.toFixed(2),
            name: '0% <= DGM < 5%'
          };
          break;
        case productDGM >= 0.05 && productDGM < 0.15:
          range = {
            unique: productDGM.toFixed(2),
            name: '5% <= DGM < 15%'
          };
          break;
        case productDGM >= 0.15 && productDGM < 0.25:
          range = {
            unique: productDGM.toFixed(2),
            name: '15% <= DGM < 25%'
          };
          break;
        case productDGM >= 0.25 && productDGM < 0.5:
          range = {
            unique: productDGM.toFixed(2),
            name: '25% <= DGM < 50%'
          };
          break;
        case productDGM >= 0.5:
          range = {
            unique: productDGM.toFixed(2),
            name: 'DGM >= 50%'
          };
          break;
      }

      if (range) {
        return {
          ...acc,
          [productId.toString()]: range
        };
      }

      return acc;
    },
        {} as Record<string, AtributeElement>
  );

  return rangeDGM;
}

export function heightByStep(height: number) {
  return height % STEP_AISLE_HEIGHT !== 0
    ? STEP_AISLE_HEIGHT * (Math.trunc(height / STEP_AISLE_HEIGHT) + 1)
    : height;
}

export function facingGroupHeight(facingGroup: FacingGroup) {
  let facingHeight =
        facingGroup.packaging.height * facingGroup.vertical_facings;
  const itemHeight = orientationFacing(
    facingGroup.orientation_code,
    facingGroup.packaging.width,
    facingGroup.packaging.height,
    facingGroup.packaging.depth
  ).height;
  const itemCappedHeight = orientationFacing(
    facingGroup.capped_orientation_code,
    facingGroup.packaging.width,
    facingGroup.packaging.height,
    facingGroup.packaging.depth
  ).height;

  if (facingGroup.cap_nested === Nesting.YNested) {
    facingHeight =
            itemHeight +
            (facingGroup.vertical_facings - 1) * facingGroup.packaging.nested_height;
  }

  if (facingGroup.cap_nested === Nesting.YCapped) {
    facingHeight =
            itemHeight * facingGroup.vertical_facings +
            itemCappedHeight * facingGroup.capped_vertical_facings;
  }

  return facingGroup.y + facingHeight;
}

export function updateBlocks(
  blocks: Array<Block>,
  segment: number,
  operation: number
): Array<Block> {
  return blocks.map((block, index) => {
    const from = index ? blocks[index - 1].num_segments + 1 : 1;
    const to = from + block.num_segments - 1;
    const numSegments = segment >= from && segment <= to
      ? block.num_segments + operation
      : block.num_segments;

    return {
      ...block,
      num_segments: numSegments,
      width: numSegments * SEGMENT_WIDTH,
    };
  });
}

export function orientationFacing(
  orientation: number,
  width: number,
  height: number,
  depth: number
) {
  switch (orientation) {
    case 1:
    case 7:
    case 13:
    case 19:
      return {
        width: height,
        height: width,
        depth
      };
    case 2:
    case 8:
    case 14:
    case 20:
      return {
        width: depth,
        height,
        depth: width
      };
    case 3:
    case 9:
    case 15:
    case 21:
      return {
        width: height,
        height: depth,
        depth: width
      };
    case 4:
    case 10:
    case 16:
    case 22:
      return {
        width,
        height: depth,
        depth: height
      };
    case 5:
    case 11:
    case 17:
    case 23:
      return {
        width: depth,
        height: width,
        depth: height
      };
    default:
      return {
        width,
        height,
        depth
      };
  }
}
